import React, { useEffect, useMemo, useState } from 'react';
import Logo from './Logo';
import { astraService, normalizePhone } from '../services/firebase';
import { writePayerSetupResumeState } from '../services/payerSetupState';

const AstraPayerReturnPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [message, setMessage] = useState('Finishing your bank pay setup...');

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const token = String(params.get('token') || '').trim();
  const phone = normalizePhone(String(params.get('phone') || ''));
  const step = String(params.get('step') || '').trim();
  const plaidAccountId = String(params.get('plaidAccountId') || '').trim() || null;
  const authorizationCode = String(
    params.get('authorizationCode')
    || params.get('authorization_code')
    || params.get('code')
    || ''
  ).trim();
  const explicitError = String(params.get('error_description') || params.get('error') || '').trim();

  useEffect(() => {
    const finalize = async () => {
      if (!token || !phone) {
        setStatus('error');
        setMessage('Missing payer setup context. Please return to the payment link and try again.');
        return;
      }

      if (explicitError) {
        setStatus('error');
        setMessage(explicitError);
        return;
      }

      try {
        const redirectParams = new URLSearchParams(window.location.search);
        ['authorizationCode', 'authorization_code', 'code', 'state', 'error', 'error_description'].forEach((key) => redirectParams.delete(key));
        const redirectUri = `${window.location.origin}${window.location.pathname}${redirectParams.toString() ? `?${redirectParams.toString()}` : ''}`;

        if (authorizationCode) {
          await astraService.exchangeAuthorizationCode({
            phone,
            authorizationCode,
            redirectUri,
          });
        }

        if (step === 'payment_authorization' && plaidAccountId) {
          await astraService.registerPaymentBank({
            phone,
            plaidAccountId,
          });
        }

        writePayerSetupResumeState({
          token,
          phone,
          nextStep: step === 'identity' ? 'payment_authorization' : 'confirm',
          selectedPaymentBankId: plaidAccountId,
          updatedAt: Date.now(),
        });

        window.location.replace(`/pay/${encodeURIComponent(token)}?setup_resumed=1`);
      } catch (error: any) {
        setStatus('error');
        setMessage(error?.message || 'Unable to finish Astra setup. Please return to the payment link and try again.');
      }
    };

    void finalize();
  }, [authorizationCode, explicitError, params, phone, plaidAccountId, step, token]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-6 space-y-8">
      <div className="text-center space-y-4 max-w-md">
        {status === 'loading' ? (
          <div className="mx-auto w-16 h-16 rounded-full border-b-2 border-teal-600 animate-spin" />
        ) : (
          <div className="mx-auto w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-red-500 text-2xl">!</div>
        )}
        <h1 className="text-2xl font-bold text-slate-900">
          {status === 'loading' ? 'Returning to Clero' : 'Setup interrupted'}
        </h1>
        <p className="text-slate-500">{message}</p>
        {status === 'error' && token && (
          <button
            type="button"
            onClick={() => window.location.assign(`/pay/${encodeURIComponent(token)}`)}
            className="inline-flex items-center justify-center rounded-full bg-slate-900 px-5 py-3 text-sm font-bold text-white"
          >
            Back to payment link
          </button>
        )}
      </div>
      <Logo size="sm" className="opacity-20 grayscale" />
    </div>
  );
};

export default AstraPayerReturnPage;
